// import styles from "./style.module.scss";
import React, { useEffect, useState } from 'react';
import PageHeading from '../../components/PageHeading';
import { Link, useSearchParams } from 'react-router-dom';
import { camelToSentence } from 'camel-to-sentence';
import { ANNOTATOR_API } from '../../api-handle';
import { LOCALSTORAGE_TOKEN_LABEL } from '../../constants';
import ReactAudioPlayer from 'react-audio-player';
import useAllEventClasses from '../../hooks/useAllEventClasses';
import { getHighestEventClass, launchToast } from '../../utils';
import useAllEventsList from '../../hooks/useAllEventsList';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context';

/**
 * EventDetails component:
 * This component shows all the details of a single event identified by the
 * event id taken from url params of the page. It also provides the action
 * control to annotate that event.
 */
export default function EventDetails() {
  const [searchParams] = useSearchParams();
  const [eventData, setEventData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const { allEventClasses, loadingAllEventClasses } = useAllEventClasses();
  const { allEvents, allDevices, loadingGlobalAssets } = useAppContext();
  const [eventDevice, setEventDevice] = useState(null);
  const [allEventIDsList, setAllEventIDsList] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [curEventIdInd, setCurEventIdInd] = useState(0);
  const [comment, setComment] = useState('');
  const [lastAnnotatorUser, setLastAnnotatorUser] = useState('');
  const navigate = useNavigate();

  const handleSubmitAnnotate = () => {
    setLoadingSubmit(true);
    ANNOTATOR_API.annotate_event(
      localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
      selectedClasses.join(','),
      searchParams.get('id'),
      comment
    )
      .then((res) => {
        setLoadingSubmit(false);
        launchToast.success('Annotation submitted successfully!');
      })
      .catch((err) => {
        setLoadingSubmit(false);
        launchToast.error('Annotation failed!');
        console.log(err);
      });
  };

  useEffect(() => {
    if (!loadingGlobalAssets && allEvents && allDevices && eventData) {
      setAllEventIDsList(allEvents.map((e) => e.id));
      setEventDevice(allDevices.find((d) => d.id === eventData.deviceId));
    }
  }, [allEvents, loadingGlobalAssets, allDevices, eventData]);

  useEffect(() => {
    if (searchParams && searchParams.get('id')) {
      setIsLoading(true);
      ANNOTATOR_API.getAnnotationsOfEvent(
        window.localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
        searchParams.get('id')
      )
        .then((res) => {
          setEventData(res.data);
          if (res.data.annotation) {
            setSelectedClasses(res.data.annotation.annotations.split(','));
            setComment(res.data.annotation.comments);
            ANNOTATOR_API.getUserByID(
              window.localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
              eventData.annotation.annotatedBy
            )
              .then((res2) => {
                setLastAnnotatorUser(
                  res2.data.first_name + ' ' + res2.data.last_name
                );
                setIsLoading(false);
              })
              .catch((err2) => {
                setIsLoading(false);
                console.log(err2);
              });
          } else {
            setSelectedClasses([]);
            setComment('');
            setIsLoading(false);
          }
        })
        .catch((err) => {
          setIsLoading(false);
          console.log(err);
          navigate('/404');
        });
    }
  }, [searchParams]);

  const handleDeleteEvent = () => {
    ANNOTATOR_API.deleteEvent(
      localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
      searchParams.get('id')
    )
      .then((res) => {
        launchToast.success('Event deleted successfully!');
        curEventIdInd !== allEventIDsList.length - 1
          ? setCurEventIdInd(curEventIdInd + 1)
          : setCurEventIdInd(curEventIdInd);
        navigate(
          curEventIdInd === allEventIDsList.length - 1
            ? `/event-details?id=${allEventIDsList[curEventIdInd]}`
            : `/event-details?id=${allEventIDsList[curEventIdInd + 1]}`
        );
      })
      .catch((err) => {
        launchToast.error('Event deletion failed!');
        console.log(err);
      });
  };

  return (
    <div className="container">
      <div className="bg-white rounded-15 shadow-sm px-3 pb-3">
        <PageHeading title={'Event Details'} />
        {loadingGlobalAssets && (
          <div className="py-2 text-center">Loading Events Navigator...</div>
        )}
        {!loadingGlobalAssets && allEventIDsList.length > 0 && (
          <div className="d-flex justify-content-center align-items-center gap-3 pb-3">
            <Link
              to={
                curEventIdInd === 0
                  ? `/event-details?id=${allEventIDsList[curEventIdInd]}`
                  : `/event-details?id=${allEventIDsList[curEventIdInd - 1]}`
              }
              className={
                'btn btn-secondary ' + (curEventIdInd === 0 ? 'disabled' : '')
              }
              onClick={() => {
                curEventIdInd !== 0
                  ? setCurEventIdInd(curEventIdInd - 1)
                  : setCurEventIdInd(curEventIdInd);
              }}
            >
              Prev <i className="bi bi-arrow-left ms-2"></i>
            </Link>
            <Link
              to={
                curEventIdInd === allEventIDsList.length - 1
                  ? `/event-details?id=${allEventIDsList[curEventIdInd]}`
                  : `/event-details?id=${allEventIDsList[curEventIdInd + 1]}`
              }
              onClick={() => {
                curEventIdInd !== allEventIDsList.length - 1
                  ? setCurEventIdInd(curEventIdInd + 1)
                  : setCurEventIdInd(curEventIdInd);
              }}
              className={
                'btn btn-secondary ' +
                (curEventIdInd === allEventIDsList.length - 1 ? 'disabled' : '')
              }
            >
              Next <i className="bi bi-arrow-right ms-2"></i>
            </Link>
          </div>
        )}
        {!isLoading ? (
          <div className="row">
            <div className="col-12 col-md-5 p-2">
              <div className="card">
                <div className="card-header">
                  <div className="fw-bold card-title">Annotate</div>
                </div>
                <div className="card-body">
                  <ReactAudioPlayer
                    src={eventData.soundFileUrl}
                    controls
                    className="w-100"
                  />
                  <div className="row mt-3">
                    {loadingAllEventClasses ? (
                      <div className="d-flex justify-content-center gap-1">
                        <div
                          className="spinner-grow spinner-grow-sm text-primary"
                          role="status"
                        ></div>
                        <div
                          className="spinner-grow spinner-grow-sm text-primary"
                          role="status"
                        ></div>
                        <div
                          className="spinner-grow spinner-grow-sm text-primary"
                          role="status"
                        ></div>
                      </div>
                    ) : (
                      <>
                        {allEventClasses.map((i, index) => (
                          <div className="col-6" key={index}>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                value={i}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedClasses([...selectedClasses, i]);
                                  } else {
                                    setSelectedClasses(
                                      selectedClasses.filter((j) => j !== i)
                                    );
                                  }
                                }}
                                checked={selectedClasses.includes(i)}
                              />
                              <label className="form-check-label">{i}</label>
                            </div>
                          </div>
                        ))}
                        <div className="form-group">
                          <label>Comments</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                          />
                        </div>
                        <div className="d-flex mt-3 gap-3">
                          <button
                            className={`btn btn-primary  ${
                              selectedClasses.length === 0 || loadingSubmit
                                ? 'disabled'
                                : ''
                            }`}
                            disabled={
                              selectedClasses.length === 0 || loadingSubmit
                            }
                            onClick={() => {
                              handleSubmitAnnotate();
                            }}
                          >
                            {loadingSubmit ? (
                              <div
                                className="spinner-grow spinner-grow-sm text-light mx-4"
                                role="status"
                              ></div>
                            ) : (
                              <div>
                                <i className="bi bi-pen-fill me-2"></i> Annotate
                              </div>
                            )}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setSelectedClasses([]);
                            }}
                          >
                            <i className="bi bi-x-lg me-md-2"></i> Clear
                            <span className="d-none d-md-inline">
                              {' '}
                              Selection
                            </span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-7 p-2">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div className="fw-bold card-title">Event Metadata</div>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteEvent}
                  >
                    <i className="bi bi-trash me-md-2"></i>{' '}
                    <span className="d-none d-md-inline">Delete Event</span>
                  </button>
                </div>
                <div className="card-body">
                  {eventData && (
                    <>
                      {Object.keys(eventData).map((key) => {
                        if (
                          key !== 'eventDetails' &&
                          key !== 'id' &&
                          key !== 'annotations' &&
                          key !== 'deviceId' &&
                          key !== 'soundFileName' &&
                          key !== 'soundFileUrl' &&
                          key !== 'annotation'
                        ) {
                          return (
                            <div key={key} className="card-text">
                              <span className="fw-bold">
                                {camelToSentence(key)}:
                              </span>{' '}
                              <span>{eventData[key]}</span>
                            </div>
                          );
                        }
                        return null;
                      })}
                      <div className="card-text">
                        <span className="fw-bold">Sound File:</span>{' '}
                        <a
                          className="link-primary"
                          href={eventData['soundFileUrl']}
                          download
                          title={
                            eventData['soundFileName'].split('/')[
                              eventData['soundFileName'].split('/').length - 1
                            ]
                          }
                        >
                          {
                            eventData['soundFileName'].split('/')[
                              eventData['soundFileName'].split('/').length - 1
                            ]
                          }
                        </a>
                      </div>
                      <div className="card-text">
                        <span className="fw-bold">IOT Device Prediction:</span>{' '}
                        <span>
                          {
                            getHighestEventClass(eventData.eventDetails)
                              .className
                          }
                        </span>
                      </div>
                      <div className="card-text">
                        <span className="fw-bold">Prediction Score:</span>{' '}
                        <span>
                          {
                            getHighestEventClass(eventData.eventDetails)
                              .predictionScore
                          }
                        </span>
                      </div>
                      {eventDevice && (
                        <>
                          <div className="card-text">
                            <span className="fw-bold">Device Name:</span>{' '}
                            <span>{eventDevice.name}</span>
                          </div>
                          <div className="card-text">
                            <span className="fw-bold">Device Serial No.:</span>{' '}
                            <span>{eventDevice.serialNumber}</span>
                          </div>
                        </>
                      )}
                      {eventData.annotation && (
                        <>
                          <div className="card-text">
                            <span className="fw-bold">Last Annotated at:</span>{' '}
                            <span>{eventData.annotation.modifiedAt}</span>
                          </div>
                          <div className="card-text">
                            <span className="fw-bold">Last Annotated by:</span>{' '}
                            <span>{lastAnnotatorUser}</span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <br />
                  <Link
                    to={`/annotations?eventID=${eventData.id}`}
                    className="card-link btn btn-dark"
                  >
                    View Annotations <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-5 d-flex flex-column align-items-center">
            <div className="d-flex justify-content-center gap-1">
              <div
                className="spinner-grow spinner-grow-sm text-primary"
                role="status"
              ></div>
              <div
                className="spinner-grow spinner-grow-sm text-primary"
                role="status"
              ></div>
              <div
                className="spinner-grow spinner-grow-sm text-primary"
                role="status"
              ></div>
            </div>
            <br />
            <div className="fw-bold text-primary">Loading data...</div>
          </div>
        )}
      </div>
    </div>
  );
}
